/**
 * チバリヨ - P2 目標設定画面
 *
 * 機能:
 *   - メンバー (sales/active) × 月 (半期6ヶ月) のマトリクス
 *   - 各セルにアポイント目標・見積目標を入力
 *   - セルクリックで編集モーダル(理由メモ必須)
 *   - 変更履歴は goalsHistory に append
 *   - 履歴ボタンで履歴サイドオーバーレイ表示
 *
 * 安全装置:
 *   - 理由メモ必須(空欄保存不可)
 *   - 変更ない場合は何もしない
 *   - goalId = "{memberId}_{yearMonth}" 形式で setDoc
 */

import {
  doc,
  collection,
  getDoc,
  setDoc,
  getDocs,
  query,
  orderBy,
  addDoc,
  limit,
  serverTimestamp
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';

import { auth, db } from './firebase-init.js';

// テンプレチップ候補
const REASON_TAGS = [
  '期初設定',
  '上方修正',
  '下方修正',
  '業務量変化',
  '市場状況',
  'その他'
];

// 状態
let halfStart = null;     // "2025-12"
let halfEnd = null;       // "2026-05"
let yearMonths = [];      // ["2025-12", "2026-01", ..., "2026-05"]
let salesMembers = [];    // role=sales && active=true
let goalsMap = {};        // { "memberId_yearMonth": { appointmentTarget, quoteTarget } }

// =====================================================================
// 公開関数
// =====================================================================
export async function renderGoalsPage(container) {
  container.innerHTML = `
    <div class="p2-header">
      <h1>目標設定</h1>
      <div class="p2-header-actions">
        <button id="p2-history-btn" class="btn btn-ghost">変更履歴</button>
      </div>
    </div>
    <div id="p2-error" class="p2-error" style="display:none;"></div>
    <div id="p2-success" class="p2-success" style="display:none;"></div>
    <div id="p2-content" class="p2-content">
      <div class="p2-loading">読み込み中…</div>
    </div>
  `;

  document.getElementById('p2-history-btn').addEventListener('click', openHistoryPanel);

  await loadData();
  renderMatrix();
}

// =====================================================================
// データロード
// =====================================================================
async function loadData() {
  try {
    // config から半期を取得
    const cSnap = await getDoc(doc(db, 'config', 'system'));
    if (!cSnap.exists()) {
      showError('config/system が存在しません');
      return;
    }
    const config = cSnap.data();
    halfStart = config.halfStart;
    halfEnd = config.halfEnd;
    yearMonths = buildYearMonths(halfStart, halfEnd);

    // sales メンバー
    const mSnap = await getDocs(query(collection(db, 'members'), orderBy('order', 'asc')));
    salesMembers = mSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((m) => m.active !== false && m.chibariyoAccess !== false && m.role === 'sales');

    // 既存目標
    goalsMap = {};
    const gSnap = await getDocs(collection(db, 'goals'));
    gSnap.docs.forEach((d) => {
      goalsMap[d.id] = d.data();
    });
  } catch (err) {
    showError('読み込みに失敗しました: ' + err.message);
  }
}

function buildYearMonths(start, end) {
  if (!start || !end) return [];
  const result = [];
  let [sy, sm] = start.split('-').map(Number);
  const [ey, em] = end.split('-').map(Number);
  for (let i = 0; i < 24; i++) {
    const ym = `${sy}-${String(sm).padStart(2, '0')}`;
    result.push(ym);
    if (sy === ey && sm === em) break;
    sm++;
    if (sm > 12) { sm = 1; sy++; }
  }
  return result;
}

// =====================================================================
// マトリクス描画
// =====================================================================
function renderMatrix() {
  if (yearMonths.length === 0 || salesMembers.length === 0) {
    document.getElementById('p2-content').innerHTML = `
      <div class="p2-empty">
        ${salesMembers.length === 0 ? 'sales 権限のアクティブメンバーがいません' : '期間設定が無効です'}
      </div>
    `;
    return;
  }

  const content = document.getElementById('p2-content');
  content.innerHTML = `
    <div class="p2-period-info">
      期間: <strong>${escapeHtml(halfStart)}</strong> 〜 <strong>${escapeHtml(halfEnd)}</strong>
      (${yearMonths.length}ヶ月)
    </div>

    <div class="p2-matrix-wrapper">
      <table class="p2-matrix">
        <thead>
          <tr>
            <th class="p2-th-member">メンバー</th>
            <th class="p2-th-indicator">指標</th>
            ${yearMonths.map((ym) => `<th class="p2-th-month">${formatYearMonth(ym)}</th>`).join('')}
            <th class="p2-th-total">半期計</th>
          </tr>
        </thead>
        <tbody>
          ${salesMembers.map((m) => renderMemberRows(m)).join('')}
        </tbody>
      </table>
    </div>
    <p class="p2-hint">セルをクリックすると目標値を編集できます。変更には理由メモが必須です。</p>
  `;

  bindCellEvents();
}

function renderMemberRows(member) {
  const apptTotals = yearMonths.reduce((sum, ym) => sum + getGoal(member.id, ym, 'appointmentTarget'), 0);
  const quoteTotals = yearMonths.reduce((sum, ym) => sum + getGoal(member.id, ym, 'quoteTarget'), 0);

  return `
    <tr class="p2-row-appointment">
      <td class="p2-td-member" rowspan="2">
        <div class="p2-member-name">${escapeHtml(member.name)}</div>
        ${member.jobTitle ? `<div class="p2-member-job">${escapeHtml(member.jobTitle)}</div>` : ''}
      </td>
      <td class="p2-td-indicator">アポ</td>
      ${yearMonths.map((ym) => renderCell(member.id, ym, 'appointmentTarget')).join('')}
      <td class="p2-td-total">${apptTotals}</td>
    </tr>
    <tr class="p2-row-quote">
      <td class="p2-td-indicator">見積</td>
      ${yearMonths.map((ym) => renderCell(member.id, ym, 'quoteTarget')).join('')}
      <td class="p2-td-total">${quoteTotals}</td>
    </tr>
  `;
}

function renderCell(memberId, yearMonth, indicator) {
  const value = getGoal(memberId, yearMonth, indicator);
  const isUnset = value === 0 && !goalsMap[`${memberId}_${yearMonth}`];
  return `
    <td class="p2-td-cell ${isUnset ? 'is-unset' : ''}"
        data-member-id="${memberId}"
        data-year-month="${yearMonth}"
        data-indicator="${indicator}"
        title="クリックで編集">
      ${value}
    </td>
  `;
}

function getGoal(memberId, yearMonth, indicator) {
  const goalId = `${memberId}_${yearMonth}`;
  const g = goalsMap[goalId];
  if (!g) return 0;
  return Number(g[indicator] || 0);
}

function bindCellEvents() {
  document.querySelectorAll('.p2-td-cell').forEach((cell) => {
    cell.addEventListener('click', () => {
      openEditModal(cell.dataset.memberId, cell.dataset.yearMonth, cell.dataset.indicator);
    });
  });
}

// =====================================================================
// セル編集モーダル
// =====================================================================
function openEditModal(memberId, yearMonth, indicator) {
  const member = salesMembers.find((m) => m.id === memberId);
  if (!member) return;

  const goalId = `${memberId}_${yearMonth}`;
  const existing = goalsMap[goalId];
  const currentValue = existing ? Number(existing[indicator] || 0) : 0;
  const isInitial = !existing || (existing[indicator] === undefined || existing[indicator] === null);

  const indicatorLabel = indicator === 'appointmentTarget' ? 'アポイント目標' : '見積目標';

  const modal = createModal(`目標編集: ${member.name} / ${formatYearMonth(yearMonth)}`);
  modal.body.innerHTML = `
    <div class="p2-edit-info">
      <div><strong>${escapeHtml(member.name)}</strong>(${escapeHtml(yearMonth)})</div>
      <div class="p2-edit-indicator">${indicatorLabel}</div>
    </div>

    <form id="p2-edit-form" class="p2-form">
      <label class="p2-field">
        <span class="p2-field-label">現在の値</span>
        <div class="p2-readonly-value">${currentValue}</div>
      </label>

      <label class="p2-field">
        <span class="p2-field-label">新しい値 <span class="p2-required">*</span></span>
        <input type="number" name="newValue" min="0" max="999" required value="${currentValue}" autofocus>
      </label>

      <div class="p2-field">
        <span class="p2-field-label" id="p2-reason-label">変更理由 <span class="p2-required" id="p2-reason-required">*</span></span>
        <div class="p2-tag-grid">
          ${REASON_TAGS.map((tag) => `
            <label class="p2-tag-chip">
              <input type="checkbox" name="reasonTags" value="${escapeAttr(tag)}">
              <span>${escapeHtml(tag)}</span>
            </label>
          `).join('')}
        </div>
        <textarea id="p2-reason-textarea" name="reason" rows="3" required placeholder="変更の背景・理由を記入してください" minlength="2"></textarea>
        <span class="p2-field-hint" id="p2-reason-hint">理由メモは必須です(任意のタグも併用可)</span>
      </div>

      <div class="p2-form-actions">
        <button type="button" class="btn btn-ghost" id="p2-edit-cancel">キャンセル</button>
        <button type="submit" class="btn btn-primary" id="p2-edit-submit">保存</button>
      </div>
    </form>
  `;

  // タグの選択状態に応じて、理由メモの required を切り替える
  // 「期初設定」が選ばれている時は理由メモ任意、それ以外は必須
  function updateReasonRequired() {
    const initialChip = modal.body.querySelector('.p2-tag-chip input[value="期初設定"]');
    const textarea = document.getElementById('p2-reason-textarea');
    const requiredMark = document.getElementById('p2-reason-required');
    const hint = document.getElementById('p2-reason-hint');
    const isInitialChecked = initialChip && initialChip.checked;

    if (isInitialChecked) {
      textarea.removeAttribute('required');
      textarea.removeAttribute('minlength');
      textarea.placeholder = '任意(期初設定のため理由メモは不要です)';
      if (requiredMark) requiredMark.style.display = 'none';
      if (hint) hint.textContent = '「期初設定」選択時は理由メモは任意です';
    } else {
      textarea.setAttribute('required', '');
      textarea.setAttribute('minlength', '2');
      textarea.placeholder = '変更の背景・理由を記入してください';
      if (requiredMark) requiredMark.style.display = '';
      if (hint) hint.textContent = '理由メモは必須です(任意のタグも併用可)';
    }
  }

  modal.body.querySelectorAll('.p2-tag-chip input').forEach((cb) => {
    cb.addEventListener('change', updateReasonRequired);
  });
  // 初期評価(編集モーダルを開いた直後の状態)
  updateReasonRequired();

  document.getElementById('p2-edit-cancel').addEventListener('click', () => modal.close());
  document.getElementById('p2-edit-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    await handleSaveCell(modal, memberId, yearMonth, indicator, currentValue, isInitial);
  });
}

async function handleSaveCell(modal, memberId, yearMonth, indicator, oldValue, isInitial) {
  const form = document.getElementById('p2-edit-form');
  const submitBtn = document.getElementById('p2-edit-submit');
  submitBtn.disabled = true;

  const formData = new FormData(form);
  const newValue = Number(formData.get('newValue'));
  const reason = (formData.get('reason') || '').trim();
  const reasonTags = formData.getAll('reasonTags');

  if (newValue === oldValue) {
    showError('値が変更されていません');
    submitBtn.disabled = false;
    return;
  }

  // 「期初設定」タグが選ばれていれば理由メモは任意、それ以外は必須
  const isInitialTag = reasonTags.includes('期初設定');
  if (!isInitialTag && (!reason || reason.length < 2)) {
    showError('変更理由は必須です(2文字以上)。「期初設定」タグを選ぶと理由メモを省略できます。');
    submitBtn.disabled = false;
    return;
  }

  const goalId = `${memberId}_${yearMonth}`;
  const indicatorKey = indicator === 'appointmentTarget' ? 'appointment' : 'quote';

  try {
    // 既存 goal の他の指標値を保持
    const existing = goalsMap[goalId] || {};
    const goalDoc = {
      memberId,
      yearMonth,
      appointmentTarget: existing.appointmentTarget ?? 0,
      quoteTarget: existing.quoteTarget ?? 0,
      [indicator]: newValue,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser.uid
    };

    await setDoc(doc(db, 'goals', goalId), goalDoc);

    // 履歴に追加
    await addDoc(collection(db, 'goalsHistory'), {
      memberId,
      yearMonth,
      indicator: indicatorKey,
      oldValue,
      newValue,
      reason,
      reasonTags,
      changedBy: auth.currentUser.uid,
      changedAt: serverTimestamp(),
      changeType: isInitial ? 'initial' : 'update'
    });

    // ローカルを更新
    goalsMap[goalId] = goalDoc;

    modal.close();
    showSuccess('保存しました');
    renderMatrix();
  } catch (err) {
    showError('保存に失敗しました: ' + err.message);
    submitBtn.disabled = false;
  }
}

// =====================================================================
// 履歴パネル
// =====================================================================
async function openHistoryPanel() {
  const overlay = document.createElement('div');
  overlay.className = 'p2-history-overlay';
  overlay.innerHTML = `
    <div class="p2-history-panel">
      <div class="p2-history-header">
        <h3>変更履歴</h3>
        <button class="p2-history-close" type="button">×</button>
      </div>
      <div id="p2-history-body" class="p2-history-body">
        <div class="p2-loading">読み込み中…</div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => {
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
  };
  overlay.querySelector('.p2-history-close').addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  try {
    const snap = await getDocs(query(
      collection(db, 'goalsHistory'),
      orderBy('changedAt', 'desc'),
      limit(50)
    ));
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    renderHistoryItems(items);
  } catch (err) {
    document.getElementById('p2-history-body').innerHTML = `
      <div class="p2-error" style="display:block;">履歴の取得に失敗: ${escapeHtml(err.message)}</div>
    `;
  }
}

function renderHistoryItems(items) {
  const body = document.getElementById('p2-history-body');
  if (items.length === 0) {
    body.innerHTML = '<div class="p2-empty">履歴はありません</div>';
    return;
  }

  body.innerHTML = `
    <ul class="p2-history-list">
      ${items.map((h) => {
        const member = salesMembers.find((m) => m.id === h.memberId);
        const memberName = member ? member.name : h.memberId;
        const indicatorLabel = h.indicator === 'appointment' ? 'アポ' : '見積';
        const typeLabel = h.changeType === 'initial' ? '初期登録' : '変更';
        return `
          <li class="p2-history-item">
            <div class="p2-history-meta">
              <span class="p2-history-type p2-history-type-${h.changeType}">${typeLabel}</span>
              <span class="p2-history-date">${formatTimestamp(h.changedAt)}</span>
            </div>
            <div class="p2-history-main">
              <strong>${escapeHtml(memberName)}</strong> / ${escapeHtml(h.yearMonth)} / ${indicatorLabel}
            </div>
            <div class="p2-history-values">
              ${h.oldValue} → <strong>${h.newValue}</strong>
            </div>
            ${h.reasonTags && h.reasonTags.length > 0 ? `
              <div class="p2-history-tags">
                ${h.reasonTags.map((t) => `<span class="p2-history-tag">${escapeHtml(t)}</span>`).join('')}
              </div>
            ` : ''}
            <div class="p2-history-reason">${escapeHtml(h.reason || '')}</div>
          </li>
        `;
      }).join('')}
    </ul>
  `;
}

// =====================================================================
// モーダル
// =====================================================================
function createModal(title) {
  const overlay = document.createElement('div');
  overlay.className = 'p3-modal-overlay';
  overlay.innerHTML = `
    <div class="p3-modal">
      <div class="p3-modal-header">
        <h3>${escapeHtml(title)}</h3>
        <button class="p3-modal-close" type="button">×</button>
      </div>
      <div class="p3-modal-body"></div>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = () => {
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
  };
  overlay.querySelector('.p3-modal-close').addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  return {
    body: overlay.querySelector('.p3-modal-body'),
    close
  };
}

// =====================================================================
// ユーティリティ
// =====================================================================
function formatYearMonth(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return `${y}/${m}`;
}

function formatTimestamp(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function showError(msg) {
  const el = document.getElementById('p2-error');
  if (!el) { alert(msg); return; }
  el.textContent = msg;
  el.style.display = 'block';
  const succ = document.getElementById('p2-success');
  if (succ) succ.style.display = 'none';
  setTimeout(() => { el.style.display = 'none'; }, 6000);
}

function showSuccess(msg) {
  const el = document.getElementById('p2-success');
  if (!el) return;
  el.textContent = msg;
  el.style.display = 'block';
  document.getElementById('p2-error').style.display = 'none';
  setTimeout(() => { el.style.display = 'none'; }, 3000);
}

function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}
function escapeAttr(str) { return escapeHtml(str); }
